import type { Account, AccountType, AccountWithUsers, Office, ResourceUserRole, User } from '@services/dto/account';

export enum UserType {
  client,
  expert,
  provider,
}

export interface Rights {
  primaryProvider: boolean;
  accountId?: string;
  id: string;
  userType: UserType;
  accountType: AccountType;
  isGuest?: boolean;

  canViewDashboard?: boolean;
  canListAccounts?: boolean;
  canEditAccount?: boolean;
  canDeleteAccount?: boolean;
  canInviteAccount?: boolean;
  canInviteAccountExpertOnly?: boolean;
  providerPosition?: string;

  canViewSubscription?: boolean;
  canManageSubscription?: boolean;

  canClassifyProjectType?: boolean;

  canCreateProject?: boolean;
  canInviteToProject?: boolean;
  canUploadFile?: boolean;
  canViewFees?: boolean;
  /** Accept, decline or counter the provider's fee: the client's project or account Owner/Administrator. */
  canDecideFee?: boolean;

  canListOffices?: boolean;
  canCreateOffice?: boolean;
  canInviteToOffice?: boolean;
  canEditOffice?: boolean;
  canDeleteOffice?: boolean;

  canListClients?: boolean;
  canInviteClient?: boolean;
  canEditClient?: boolean;
  canDeleteClient?: boolean;

  canChat?: boolean;
  canUseAiHub?: boolean;

  canListTx?: boolean;
  canCreateTx?: boolean;
  canEditTx?: boolean;
  canDeleteTx?: boolean;

  canListDocs?: boolean;
  canCreateDoc?: boolean;
  canEditDoc?: boolean;
  canDeleteDoc?: boolean;

  // New properties for managing a single office via settings
  canManageSingleOfficeInSettings?: boolean;
  manageableOfficeIdInSettings?: string;

  // New property for AI prompt management (super admin only)
  canManageAIPrompts?: boolean;

  offices: { officeId: string; role: ResourceUserRole }[];
  clients: { clientId: string; role: ResourceUserRole }[];
  estimateApps: string[];
  projects: {
    projectId: string;
    role: ResourceUserRole;
    phases: {
      phaseId: string;
      role: ResourceUserRole;
      estimates: {
        estimateId: string;
        role: ResourceUserRole;
      }[];
    }[];
  }[];
}

export type AccountState = {
  me?: User;
  loadingMe?: boolean;
  /** /users/me failed for a reason that is not a bad session. */
  meError?: boolean;
  /** What actually went wrong, for the screen to show and for support to read back. */
  meErrorDetail?: string;

  accounts?: Account[];
  currentAccount?: Account;
  currentFullAccount?: Account;
  currentAccountWithUsers?: AccountWithUsers;
  accountWithUsers?: AccountWithUsers;
  /**
   * Why the roster could not be read, when it could not.
   *
   * Distinct from an absent roster, which means "not yet". A read that was refused has answered,
   * and anything waiting on it has to stop waiting - an unanswered read is what left an invited
   * client on a spinner with nothing on screen to explain it.
   */
  accountWithUsersError?: string;

  /**
   * The account whose roster refused this reader as a project-scoped guest.
   *
   * The server's own answer, kept because it is better than the one the account list gives. The
   * list only shows somebody is a guest when the payload happens to carry `isProjectScoped` on
   * their row; a 403 saying ProjectScopedAccess says it outright, and is what the bootstrap falls
   * back on so it can stop waiting and show them the workspace they do have.
   */
  projectScopedRefusedAccountId?: string;

  officeAccountId?: string;
  offices?: Office[];
  currentOffice?: Office;

  users?: User[];

  rights?: Rights;
};
